// ============================================================
// TERROR ENGINE — COMPANION INTERACTION  (PETTING & HEARTS + CODEX)
// ------------------------------------------------------------
// The close-up "pet your companion" moment + a quick pokedex-style
// info card. Tap the companion (via the room's Pet prompt) and a
// framed close-up appears showing the companion large, a compact
// codex entry (tier · ilk · radiant · where it came from), and the
// hearts shower when you tap it.
//
// ── WHY THIS LIVES IN THE ENGINE ────────────────────────────
// Rendered by the room layer (PlayerEntity in engine rooms; the
// Pet ProximityBubble in Plaza). One definition, used everywhere,
// so the petting + codex experience can never drift between rooms.
//
// ── HEARTS ──────────────────────────────────────────────────
// Quick & ephemeral: 3–5 rose hearts per tap, each rises/drifts and
// fades in ~1.1–1.8s, removing itself on animationend (no timers,
// no leaks). Active hearts capped so spam-tapping is safe.
//
// ── CROSS-GAME PROVENANCE (baked in now) ────────────────────
// Companions can be EARNED in Terror or TRANSFERRED in from other
// Illegal Empire games via Pierre's Exchange. Every game writes the
// SAME shape onto the shared profile so the codex entry "just works"
// no matter where a companion originated:
//
//   profile.companions[companionId] = {
//     at: <ms>,                                   // when obtained here
//     origin: { game: "<gameKey>", detail: "<how/where>", at: <ms> }
//   }
//
// gameKey ∈ GAME_LABELS below. If no origin record exists yet, we
// fall back to the Terror registry source (getUnlockHint) — so every
// existing companion still shows where it came from. When Bebecitos
// (or any game) starts exporting companions, it stamps `origin` and
// this card reads it with zero further work.
//
// ── USAGE ───────────────────────────────────────────────────
//   import CompanionCloseup from "./CompanionInteraction";
//   {open && (
//     <CompanionCloseup
//       companionId={companionId}
//       radiantTier={radiantTier}
//       profile={profile}            // optional — enables origin lookup
//       onClose={() => setOpen(false)}
//     />
//   )}
// ============================================================

import { useState, useEffect, useCallback } from "react";
import CompanionSprite, { COMPANIONS, ILK_COLORS, getUnlockHint, getCompanionProvenance } from "./CompanionSprite";
import SourceSigil from "./SourceSigil";

// ── Grimoire chrome (mirrors theme.js GM, kept local so this file
//    has no hard import dependency on the theme module) ──────────
const GM = {
  page:          "#0f0c07",
  pageWarm:      "#1a1509",
  pageMid:       "#241c0c",
  inkGold:       "#c8963c",
  inkGoldBright: "#e8b84b",
  inkGoldDim:    "#8a6520",
  vellum:        "#e8d5a3",
  vellumDim:     "#b89a5a",
  vellumFaint:   "#7a6040",
  font:          "'Cinzel', 'Palatino Linotype', serif",
};

// Rose hues for the hearts — warm affection against the dark vellum.
const HEART_HUES = ["#ff5c8a", "#ff7fa3", "#ff4d79", "#ff93b5"];

// Holo-tier → human label (the radiant sheen the companion wears).
const TIER_LABEL = {
  holo_silver: "Silver", holo_gold: "Gold", holo_void: "Void",
  holo_shadow: "Shadow", holo_jynxi: "J'ynxi", holo_depths: "Depths",
  holo_solrend: "Solrend", holo_aeternus: "A'eterni", holo_mythic: "Mythic",
};

// Tier (ascension rank) → display.
const TIER_NAME = {
  escling: "Escling", beb: "Beb", champion: "Champion",
  lord: "Lord", mythic: "Mythic",
};

// Illegal Empire game keys → display names (cross-game provenance).
const GAME_LABELS = {
  terror: "Terror", bebecitos: "Bebecitos", snake_eyes: "Snake Eyes",
  pier: "W'ily's Pier", moonshine: "Moonshine Masters",
  sol_survivor: "Sol Survivor", echo_skew: "ECHO·SKEW", swat: "S'wat",
};

// Resolve where a companion came from. Prefers a per-profile origin
// stamp (cross-game aware); falls back to the Terror registry source.
function resolveOrigin(companionId, profile) {
  const o = profile?.companions?.[companionId]?.origin;
  if (o && o.game) {
    return { game: GAME_LABELS[o.game] || o.game, detail: o.detail || null };
  }
  return { game: "Terror", detail: getUnlockHint(companionId) };
}

// ── Heart glyph — crisp SVG, themeable, no emoji-rendering drift ─
export function HeartSvg({ color = "#ff5c8a", size = 28 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
        fill={color}
      />
    </svg>
  );
}

// ── One-time keyframe injection (id-guarded) ────────────────────
const STYLE_ID = "ttc-companion-interaction-keyframes";
function injectKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
@keyframes ttcCiFade {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes ttcCiRise {
  from { opacity: 0; transform: translateY(18px) scale(0.96); }
  to   { opacity: 1; transform: translateY(0)    scale(1); }
}
@keyframes ttcCiBob {
  0%,100% { transform: translateY(0); }
  50%     { transform: translateY(-9px); }
}
@keyframes ttcHeartFloat {
  0%   { opacity: 0; transform: translate(-50%, 12px)
           scale(calc(var(--sc, 1) * 0.5)) rotate(0deg); }
  16%  { opacity: 1; }
  100% { opacity: 0;
         transform: translate(calc(-50% + var(--drift, 0px)), -156px)
           scale(var(--sc, 1)) rotate(var(--rot, 0deg)); }
}
@keyframes ttcCiPunch {
  0%   { transform: scale(1); }
  40%  { transform: scale(1.12); }
  100% { transform: scale(1); }
}`;
  document.head.appendChild(el);
}

// ── Small labeled codex row ─────────────────────────────────────
function InfoRow({ label, value, valueColor }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "baseline",
      gap: 12,
      padding: "5px 0",
      borderTop: `1px solid ${GM.inkGold}14`,
    }}>
      <span style={{
        color: GM.inkGoldDim,
        fontSize: 9,
        letterSpacing: 2,
        textTransform: "uppercase",
        flexShrink: 0,
      }}>{label}</span>
      <span style={{
        color: valueColor || GM.vellum,
        fontSize: 12.5,
        textAlign: "right",
        lineHeight: 1.35,
      }}>{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CompanionCloseup — the petting overlay + codex entry
// ─────────────────────────────────────────────────────────────
export default function CompanionCloseup({ companionId, radiantTier = null, profile = null, onClose, onPet = null, docked = false }) {
  const [hearts, setHearts] = useState([]);
  const [punchKey, setPunchKey] = useState(0); // bump to re-trigger press punch
  const [petCount, setPetCount] = useState(0); // Track petting for achievements

  useEffect(() => { injectKeyframes(); }, []);

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Spawn a small burst of hearts around the companion.
  const pet = useCallback((e) => {
    if (e) e.stopPropagation();
    const burst = 3 + Math.floor(Math.random() * 3); // 3–5
    const stamp = Date.now();
    const fresh = Array.from({ length: burst }, (_, i) => ({
      id: `${stamp}_${i}_${Math.random().toString(36).slice(2, 6)}`,
      left: 50 + (Math.random() * 56 - 28),       // % across the stage
      drift: Math.round(Math.random() * 64 - 32), // px horizontal drift
      dur: 1100 + Math.random() * 700,            // ms
      scale: 0.7 + Math.random() * 0.7,
      rot: Math.round(Math.random() * 44 - 22),   // deg
      hue: HEART_HUES[Math.floor(Math.random() * HEART_HUES.length)],
    }));
    // Cap active hearts so rapid tapping can't grow the array unbounded.
    setHearts((h) => [...h, ...fresh].slice(-48));
    setPunchKey((k) => k + 1);
    
    // Track petting and fire callback (for achievements)
    const newCount = petCount + 1;
    setPetCount(newCount);
    if (onPet) {
      onPet(newCount); // callback receives total count; caller decides what to grant
    }
  }, [petCount, onPet]);

  const removeHeart = useCallback((id) => {
    setHearts((h) => h.filter((x) => x.id !== id));
  }, []);

  const companion = companionId ? COMPANIONS[companionId] : null;
  if (!companion) return null; // null-safe: nothing to pet

  // ── Codex fields ──────────────────────────────────────────
  const ilk         = companion.ilk || "Neutral";
  const ilkColor    = (ILK_COLORS[ilk] && ILK_COLORS[ilk].primary) || GM.inkGold;
  const tierName    = TIER_NAME[companion.tier] || companion.tier || "—";
  const radiantName = radiantTier ? (TIER_LABEL[radiantTier] || radiantTier) : null;
  const origin      = resolveOrigin(companionId, profile);

  return (
    <div
      onClick={onClose}
      style={{
        position: docked ? "absolute" : "fixed",
        inset: 0,
        zIndex: docked ? 20 : 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: docked ? "transparent" : "rgba(8,6,3,0.82)",
        backdropFilter: docked ? "none" : "blur(3px)",
        WebkitBackdropFilter: docked ? "none" : "blur(3px)",
        animation: "ttcCiFade 0.25s ease",
        padding: docked ? 8 : 16,
      }}
    >
      {/* Panel — clicks inside don't close (stage handles petting) */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: docked ? "min(96vw, 440px)" : "min(88vw, 366px)",
          maxHeight: docked ? "100%" : "92vh",
          overflowY: "auto",
          background: docked
            ? `linear-gradient(160deg, ${GM.pageWarm}f2, ${GM.page}f2)`
            : `linear-gradient(160deg, ${GM.pageWarm}, ${GM.page})`,
          border: `1px solid ${GM.inkGold}`,
          borderRadius: 14,
          padding: docked ? "12px 16px 12px" : "22px 18px 18px",
          boxShadow: `0 0 16px ${GM.inkGold}33, inset 0 1px 0 ${GM.inkGold}22, 0 18px 60px rgba(0,0,0,0.6)`,
          animation: "ttcCiRise 0.28s ease",
          fontFamily: GM.font,
          textAlign: "center",
        }}
      >
        {/* Close × */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute",
            top: 8,
            right: 10,
            width: 30,
            height: 30,
            background: "transparent",
            border: `1px solid ${GM.inkGoldDim}`,
            borderRadius: 8,
            color: GM.vellumDim,
            fontSize: 16,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ×
        </button>

        {/* Name + provenance mark — the diamond placeholder fulfilled.
            The sigil this soul wears + its own origin lore (unique per
            companion, never mechanical unlock text). Collection-bonded
            companions inherit their card's provenance mark. */}
        <div style={{
          color: GM.vellum,
          fontSize: 20,
          letterSpacing: 1.5,
          fontWeight: 600,
          textShadow: `0 0 12px ${GM.inkGold}44`,
        }}>
          {companion.name}
        </div>
        {(() => {
          const prov = getCompanionProvenance(companionId, profile);
          return (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: 4, margin: "6px 0 2px",
            }}>
              <SourceSigil source={prov.sigil || "unknown_origin"} size={44} />
              <div style={{
                color: GM.inkGold,
                fontSize: 10,
                fontStyle: "italic",
                letterSpacing: 0.8,
                lineHeight: 1.5,
                maxWidth: 250,
                textAlign: "center",
                textShadow: `0 0 8px ${GM.inkGold}44`,
              }}>
                {prov.lore}
              </div>
            </div>
          );
        })()}

        {/* Stage — tap anywhere here to pet ───────────────────── */}
        <div
          onPointerDown={pet}
          style={{
            position: "relative",
            height: docked ? 150 : 240,
            margin: "4px auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            touchAction: "manipulation",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
        >
          {/* Warm radial glow behind the companion */}
          <div style={{
            position: "absolute",
            width: docked ? 150 : 220,
            height: docked ? 150 : 220,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${GM.inkGold}22 0%, transparent 68%)`,
            pointerEvents: "none",
          }} />

          {/* Companion — gentle idle bob + press punch */}
          <div
            key={punchKey}
            style={{
              position: "relative",
              animation: "ttcCiPunch 0.32s ease",
              pointerEvents: "none",
            }}
          >
            <div style={{ animation: "ttcCiBob 2.6s ease-in-out infinite" }}>
              <CompanionSprite
                companionId={companionId}
                size={docked ? 128 : 188}
                radiantTier={radiantTier}
              />
            </div>
          </div>

          {/* Floating hearts — each removes itself on animationend */}
          {hearts.map((h) => (
            <span
              key={h.id}
              onAnimationEnd={() => removeHeart(h.id)}
              style={{
                position: "absolute",
                left: `${h.left}%`,
                bottom: "44%",
                transform: "translate(-50%, 0)",
                animation: `ttcHeartFloat ${h.dur}ms ease-out forwards`,
                filter: `drop-shadow(0 0 6px ${h.hue}aa)`,
                pointerEvents: "none",
                willChange: "transform, opacity",
                "--drift": `${h.drift}px`,
                "--rot": `${h.rot}deg`,
                "--sc": h.scale,
              }}
            >
              <HeartSvg color={h.hue} size={26} />
            </span>
          ))}
        </div>

        {/* ── CODEX ENTRY — quick pokedex card ─────────────────── */}
        <div style={{
          textAlign: "left",
          background: `${GM.page}cc`,
          border: `1px solid ${GM.inkGold}33`,
          borderRadius: 10,
          padding: "4px 12px 8px",
          margin: "6px 0 10px",
        }}>
          <InfoRow label="Tier" value={tierName} />
          <InfoRow label="Ilk" value={ilk} valueColor={ilkColor} />
          <InfoRow
            label="Radiant"
            value={radiantName || "—"}
            valueColor={radiantName ? GM.inkGoldBright : GM.vellumFaint}
          />
          <InfoRow
            label="Origin"
            value={origin.detail ? `${origin.game} · ${origin.detail}` : origin.game}
          />
        </div>

        {/* Flavor */}
        {companion.description && (
          <div style={{
            color: GM.vellumDim,
            fontSize: 12.5,
            fontStyle: "italic",
            lineHeight: 1.5,
            padding: "0 4px",
            margin: "0 0 10px",
          }}>
            {companion.description}
          </div>
        )}

        {/* Hint */}
        <div style={{
          color: GM.vellumFaint,
          fontSize: 11,
          letterSpacing: 2,
        }}>
          tap to pet
        </div>
      </div>
    </div>
  );
}
